import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tabs,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Add,
  AttachMoney,
  Cancel,
  CheckCircle,
  Delete,
  InfoOutlined,
  Inventory2,
  LocalShipping,
  MenuBook,
  ReportProblem,
  Search,
} from '@mui/icons-material';
import { FC, useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { DetailPageTemplate } from '../../../components/page-templates/detail-page/DetailPageTemplate';
import { InsightTile } from '../../../components/shared/tiles/InsightTile';
import { Pill } from '../../../components/shared/Pill';
import { ConfirmationDialog } from '../../../components/shared/dialogs/ConfirmationDialog';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Pagination from '../../../components/forms/Pagination';
import {
  MarketItem,
  MarketItemUom,
  Instance,
  Market,
  MarketCatalogue,
  MarketOrderType,
  MarketCategory,
  UomType,
  client,
  MarketSupplier,
} from '../../../app/services/api/orderManagementClient';
import StoreLevelMOQGrid from './StoreLevelMOQGrid';
import LinkedItems from './components/LinkedItems';
import AllergensTab from './components/AllergensTab';
import IngredientsTab from './components/IngredientsTab';
import NutritionalDataTab from './components/NutritionalDataTab';
import DietaryAttributesTab from './components/DietaryAttributesTab';
import { ProductIngredient, AllergenStatus } from './types';
import useLogError from '../../../hooks/useLogError';
import { applyRoundAndFormat, convertFromJsonNetDate, convertToJsonNetDate } from '../../../utils';
import dayjs, { Dayjs } from 'dayjs';
import { UserContext } from '../../../components/shared/useUser';
import { Section } from '../../../components/ui/Section';
import { isDemoModeEnabled } from '../../../utils/featureAccess';
import { PictureTile } from '../../../components/shared/tiles/PictureTile';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';

const toDateValue = (value?: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const isDateInRange = (startDate?: string | null, endDate?: string | null): boolean => {
  const today = dayjs().startOf('day');
  const parsedStart = toDateValue(startDate);
  const parsedEnd = toDateValue(endDate);

  if (parsedStart && today.isBefore(parsedStart.startOf('day'))) {
    return false;
  }

  if (parsedEnd && today.isAfter(parsedEnd.endOf('day'))) {
    return false;
  }

  return true;
};

const ProductForm: FC = () => {
  const { t } = useTranslation('productManagement');
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logError, extractErrorMessage } = useLogError();
  const { selectedStore } = useContext(UserContext);
  // Ingredients tab has no backend persistence yet - restrict to Demo Mode (internal-only).
  const isDemoMode = isDemoModeEnabled();

  const isEditMode = Boolean(id);
  const companyId = searchParams.get('companyId');
  const customerTypeId = searchParams.get('customerTypeId');
  const searchTerm = searchParams.get('searchTerm');
  const listSort = searchParams.get('sort');
  const listDirection = searchParams.get('direction');
  const listPage = searchParams.get('page');

  const [isLoading, setIsLoading] = useState<boolean>(isEditMode);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [tabValue, setTabValue] = useState<number>(0);
  const [catalogueSearch, setCatalogueSearch] = useState<string>('');
  const [cataloguePage, setCataloguePage] = useState<number>(0);
  const [pendingBaseUomIndex, setPendingBaseUomIndex] = useState<number | null>(null);
  const [pendingOrderableUomIndex, setPendingOrderableUomIndex] = useState<number | null>(null);
  const [pendingRemoveUomIndex, setPendingRemoveUomIndex] = useState<number | null>(null);
  const [uomPage, setUomPage] = useState<number>(0);
  const [catalogueRowsPerPage, setCatalogueRowsPerPage] = useState<number>(10);
  // Raw, uncommitted text for a matrix cell currently being typed into, keyed by "row-col". The
  // cell displays this instead of the derived/rounded value while editing so the field doesn't
  // reformat itself (and knock the cursor around) on every keystroke; the edit is only parsed and
  // committed to formData - triggering the cascade recalculation - on blur.
  const [matrixEditValues, setMatrixEditValues] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<MarketItem>({
    contentId: undefined,
    companyId: parseInt(companyId || '0'),
    customerTypeId: parseInt(customerTypeId || '0'),
    itemNumber: '',
    marketItemNumber: '',
    erpInternalId: undefined,
    itemName: '',
    itemFriendlyName: '',
    marketItemName: '',
    primaryUom: '',
    orderableUom: '',
    primaryCategoryId: undefined,
    marketPrimaryCategoryId: undefined,
    primaryCategoryName: '',
    marketPrimaryCategoryName: '',
    packSize: undefined,
    caseSize: undefined,
    innerPackSize: undefined,
    weight: undefined,
    volume: undefined,
    minQuantity: undefined,
    minQuantityStartDate: '',
    minQuantityEndDate: '',
    maxQuantity: undefined,
    maxQuantityStartDate: '',
    maxQuantityEndDate: '',
    startDate: '',
    endDate: '',
    productType: '',
    parLevel: undefined,
    catalogueIds: [],
    catalogueEntries: [],
    orderTypeIds: [],
    orderTypeEntries: [],
    uoms: [],
    contentSuppliers: [],
    allergens: [],
    nutrition: [],
    dietaryAttributes: [],
  });

  const { instances, getMarkets, ensureMarketsLoaded } = useInstanceMarket();
  const markets = getMarkets(formData.companyId ? formData.companyId.toString() : '');
  const [catalogues, setCatalogues] = useState<MarketCatalogue[]>([]);
  const [orderTypes, setOrderTypes] = useState<MarketOrderType[]>([]);
  const [uomTypes, setUomTypes] = useState<UomType[]>([]);
  const [categories, setCategories] = useState<MarketCategory[]>([]);
  const [marketSuppliers, setMarketSuppliers] = useState<MarketSupplier[]>([]);
  const [useInventorySKU, setUseInventorySKU] = useState<boolean>(false);

  // Ingredients are captured locally only; no backend persistence exists yet on MarketItem.
  const [productIngredients, setProductIngredients] = useState<ProductIngredient[]>([]);

  const isParEnabled = markets.find(m => m.customerTypeId === formData.customerTypeId)?.isParEnabled === true;
  const marketCurrencyCode = markets.find(m => m.customerTypeId === formData.customerTypeId)?.currencyCode;
  const baseUomIndex = formData.uoms.findIndex((uom) => uom.isPrimaryUom);
  const orderableUomIndex = formData.uoms.findIndex((uom) => uom.isOrderableUom);
  const primaryUom = baseUomIndex >= 0 ? formData.uoms[baseUomIndex] : undefined;
  const orderableUom = orderableUomIndex >= 0 ? formData.uoms[orderableUomIndex] : undefined;
  const isOrderTypeOverrideActive = (orderTypeId: number): boolean => {
    const orderTypeEntry = formData.orderTypeEntries.find((entry) => entry.orderTypeId === orderTypeId);
    if (!orderTypeEntry || (!orderTypeEntry.startDate && !orderTypeEntry.endDate)) {
      return true;
    }

    return isDateInRange(orderTypeEntry.startDate, orderTypeEntry.endDate);
  };
  const isCatalogueActiveWithOverrides = (catalogueId: number): boolean => {
    const catalogue = catalogues.find((item) => item.customerGroupId === catalogueId);
    if (!catalogue) {
      return true;
    }

    return catalogue.orderTypeIds.every((orderTypeId) => isOrderTypeOverrideActive(orderTypeId));
  };
  const activeCatalogueCount = formData.catalogueEntries.filter((entry) => (
    formData.catalogueIds.includes(entry.catalogueId)
    && isDateInRange(entry.startDate, entry.endDate)
    && isCatalogueActiveWithOverrides(entry.catalogueId)
  )).length;
  const containsAllergenCount = (formData.allergens || []).filter((entry) => (entry.allergenStatus || '').trim().toLowerCase() === AllergenStatus.Contains.toLowerCase()).length;
  const overriddenSupplierCount = (formData.contentSuppliers || []).filter((entry) => Boolean(entry.itemNumber?.trim()) || Boolean(entry.price)).length;
  const hasUom = formData.uoms.length > 0;
  const hasPrimaryUom = Boolean(primaryUom);
  const hasOrderableUom = Boolean(orderableUom);
  const pricedUom = orderableUom?.price ? orderableUom : undefined;
  const hasCostPrice = Boolean(orderableUom && Number(orderableUom.price || 0) > 0);
  const isProductDateActive = isDateInRange(formData.startDate, formData.endDate);
  // Active-status date validation is evaluated in priority order: catalogue assignment dates,
  // then order channel overrides, then the product's own valid period - each stage is a
  // narrower/more specific window than the last, so the reason surfaced to the user should
  // reflect whichever of these fails first.
  const hasCatalogueDateValid = formData.catalogueEntries.some((entry) => (
    formData.catalogueIds.includes(entry.catalogueId) && isDateInRange(entry.startDate, entry.endDate)
  ));
  const isActive = hasCatalogueDateValid
    && activeCatalogueCount > 0
    && isProductDateActive
    && hasCostPrice
    && hasPrimaryUom
    && hasOrderableUom
    && hasUom;
  const inactiveReason = !hasCatalogueDateValid
    ? t('inactiveReasonCatalogue', 'No active catalogue assignment for the current date.')
    : activeCatalogueCount === 0
      ? t('inactiveReasonOverride', 'Order channel override is not active for the current date.')
      : !isProductDateActive
        ? t('inactiveReasonProductPeriod', 'Outside the product\u2019s valid period.')
        : !hasCostPrice
          ? t('addUomPricePrompt', 'Add a UOM price to activate the item.')
          : t('inactiveReasonUom', 'Base and orderable units of measure are required.');
  // One row per catalogue (not one row per order-channel/catalogue pair) - a catalogue can serve
  // several order channels, and previously the same catalogue/checkbox appeared once per channel,
  // so ticking it in one row made it look like several different catalogues had been selected at
  // once. Order channel names are now just listed together on the catalogue's single row.
  const catalogueRows = catalogues.map((catalogue) => ({
    catalogue,
    orderTypeNames: orderTypes
      .filter((orderType) => catalogue.orderTypeIds.includes(orderType.orderTypeId))
      .map((orderType) => orderType.name),
  }));
  const configuredOrderTypeOverrides = orderTypes
    .map((orderType) => {
      const channelCatalogueIds = catalogues
        .filter((catalogue) => catalogue.orderTypeIds.includes(orderType.orderTypeId))
        .map((catalogue) => catalogue.customerGroupId);
      const hasAssignedCatalogue = channelCatalogueIds.some((catalogueId) => formData.catalogueIds.includes(catalogueId));
      const hasDatedCatalogue = formData.catalogueEntries.some((entry) => (
        channelCatalogueIds.includes(entry.catalogueId) && (entry.startDate || entry.endDate)
      ));

      return {
        orderType,
        entry: formData.orderTypeEntries.find((item) => item.orderTypeId === orderType.orderTypeId),
        showOverride: hasAssignedCatalogue || hasDatedCatalogue,
      };
    })
    .filter(({ showOverride }) => showOverride);
  const sortedFilteredCatalogueRows = useMemo(() => {
    const normalizedSearch = catalogueSearch.trim().toLowerCase();
    return [...catalogueRows]
      .sort((a, b) => a.catalogue.name.localeCompare(b.catalogue.name))
      .filter(({ orderTypeNames, catalogue }) => {
        if (!normalizedSearch) {
          return true;
        }

        return catalogue.name.toLowerCase().includes(normalizedSearch)
          || orderTypeNames.some((name) => name.toLowerCase().includes(normalizedSearch));
      });
  }, [catalogueRows, catalogueSearch]);
  const maxCataloguePage = Math.max(0, Math.ceil(sortedFilteredCatalogueRows.length / catalogueRowsPerPage) - 1);
  const catalogueTotalPages = Math.ceil(sortedFilteredCatalogueRows.length / catalogueRowsPerPage);
  const currentCataloguePage = Math.min(cataloguePage, maxCataloguePage);
  const pagedCatalogueRows = sortedFilteredCatalogueRows.slice(
    currentCataloguePage * catalogueRowsPerPage,
    currentCataloguePage * catalogueRowsPerPage + catalogueRowsPerPage,
  );
  const uomRowsPerPage = 10;
  const maxUomPage = Math.max(0, Math.ceil(formData.uoms.length / uomRowsPerPage) - 1);
  const uomTotalPages = Math.max(1, Math.ceil(formData.uoms.length / uomRowsPerPage));
  const currentUomPage = Math.min(uomPage, maxUomPage);
  const pagedUomRows = formData.uoms
    .map((uom, idx) => ({ uom, idx }))
    .slice(currentUomPage * uomRowsPerPage, currentUomPage * uomRowsPerPage + uomRowsPerPage);
  const imageTileTitle = [formData.itemNumber, formData.itemName]
    .filter((value) => Boolean(value && value.trim()))
    .join(' - ') || t('productImage', 'Product Image');

  const getCatalogueStatus = (
    isSelected: boolean,
    startDate?: string,
    endDate?: string,
    catalogueId?: number,
  ): 'current' | 'future' | 'expired' | 'overridden' | null => {
    if (!isSelected) {
      return null;
    }

    if (catalogueId != null && !isCatalogueActiveWithOverrides(catalogueId)) {
      return 'overridden';
    }

    if (isDateInRange(startDate, endDate)) {
      return 'current';
    }

    const today = dayjs().startOf('day');
    const parsedStart = toDateValue(startDate);

    if (parsedStart && today.isBefore(parsedStart.startOf('day'))) {
      return 'future';
    }

    return 'expired';
  };

  const catalogueTileAssignedCount = catalogueRows.filter((row) => (
    formData.catalogueIds.includes(row.catalogue.customerGroupId)
  )).length;
  const catalogueTileActiveCount = catalogueRows.filter((row) => {
    const isSelected = formData.catalogueIds.includes(row.catalogue.customerGroupId);
    const catalogueEntry = formData.catalogueEntries.find((entry) => entry.catalogueId === row.catalogue.customerGroupId);
    return getCatalogueStatus(isSelected, catalogueEntry?.startDate, catalogueEntry?.endDate, row.catalogue.customerGroupId) === 'current';
  }).length;

  useEffect(() => {
    loadUomTypes(companyId || '');
    setProductIngredients([]);
    setFormData(prev => ({ ...prev, allergens: [], nutrition: [], dietaryAttributes: [] }));
    if (isEditMode && id && companyId && customerTypeId) {
      // Navigating from one product's edit page straight to another (e.g. via a Linked Items
      // link) reuses this same component instance - without forcing the loading state back on,
      // the previous product's data would stay on screen, unchanged, until the new response
      // arrives, giving no indication that anything happened.
      setIsLoading(true);
      loadProduct(id, companyId, customerTypeId);
    } else {
      setIsLoading(false);
    }
  }, [isEditMode, id, companyId, customerTypeId]);

  useEffect(() => {
    const loadCompanyConfig = async () => {
      if (!formData.companyId || formData.companyId === 0) return;
      try {
        const result = await client.getCompanyConfig(formData.companyId);
        if (result && result.data && result.data.useInventorySKU != null) {
          setUseInventorySKU(result.data.useInventorySKU);
        }
      } catch (error) {
        logError(error);
      }
    };
    loadCompanyConfig();
  }, [formData.companyId]);

  useEffect(() => {
    if (formData.companyId > 0) {
      ensureMarketsLoaded(formData.companyId.toString());
      loadCatalogues(formData.companyId.toString(), formData.customerTypeId?.toString() || '');
      loadOrderTypes(formData.companyId.toString(), formData.customerTypeId?.toString() || '');
      loadCategories(formData.companyId.toString());
      loadMarketSuppliers(formData.companyId.toString(), formData.customerTypeId?.toString() || '');
    }
  }, [formData.companyId, formData.customerTypeId, ensureMarketsLoaded]);

  useEffect(() => {
    setCataloguePage(0);
  }, [catalogueSearch, catalogueRowsPerPage]);

  useEffect(() => {
    if (cataloguePage > maxCataloguePage) {
      setCataloguePage(maxCataloguePage);
    }
  }, [cataloguePage, maxCataloguePage]);

  const loadCatalogues = async (instanceId: string, marketId: string) => {
    try {
      const response = await client.getMarketCatalogues({
        companyId: instanceId,
        customerTypeId: marketId,
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setCatalogues(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load catalogues');
    }
  };

  const loadUomTypes = async (cId: string) => {
    try {
      const response = await client.getUomTypes({
        companyId: cId || companyId || '',
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (response.success && response.data) {
        setUomTypes(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load UOM types');
    }
  };

  const loadCategories = async (instanceId: string) => {
    try {
      const response = await client.getMarketCategories({
        companyId: instanceId,
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        const sortedCategories = [...(response.data.items || [])].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        setCategories(sortedCategories);
      }
    } catch (error) {
      logError('Failed to load categories');
    }
  };

  const loadOrderTypes = async (instanceId: string, marketId: string) => {
    try {
      const response = await client.getMarketOrderTypes({
        companyId: instanceId,
        customerTypeId: marketId,
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setOrderTypes(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load order types');
    }
  };

  const loadMarketSuppliers = async (instanceId: string, marketId: string) => {
    try {
      const response = await client.getMarketSuppliers({
        companyId: instanceId,
        customerTypeId: marketId,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setMarketSuppliers(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load market suppliers');
    }
  };

  const loadProduct = async (productId: string, companyId: string, customerTypeId: string) => {
    try {
      const response = await client.getMarketItem(productId, companyId, customerTypeId);
      if (response.success && response.data) {
        // Round any pre-existing conversion data down to 2 decimal places so historic records
        // with deeper precision can't sneak past the UI's display/edit cap.
        setFormData({
          ...response.data,
          uoms: response.data.uoms.map((uom) => ({
            ...uom,
            numberOfBaseUnitsOrEaches: uom.isPrimaryUom ? 1 : roundTo2(uom.numberOfBaseUnitsOrEaches),
          })),
          allergens: (response.data.allergens || []).map((a) => ({
            ...a,
            effectiveFrom: convertFromJsonNetDate(a.effectiveFrom) || undefined,
            effectiveTo: convertFromJsonNetDate(a.effectiveTo) || undefined,
          })),
          nutrition: (response.data.nutrition || []).map((n) => ({
            ...n,
            effectiveFrom: convertFromJsonNetDate(n.effectiveFrom) || undefined,
            effectiveTo: convertFromJsonNetDate(n.effectiveTo) || undefined,
          })),
          dietaryAttributes: (response.data.dietaryAttributes || []).map((d) => ({
            ...d,
            effectiveFrom: convertFromJsonNetDate(d.effectiveFrom) || undefined,
            effectiveTo: convertFromJsonNetDate(d.effectiveTo) || undefined,
          })),
        });
      } else {
        setErrorMessage(response.message || t('loadErrorMessage'));
        setShowErrorMessage(true);
      }
    } catch (error) {
      logError('Failed to load product');
      setErrorMessage(t('loadErrorMessage'));
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof MarketItem) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleNumberInputChange = (field: keyof MarketItem) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setFormData(prev => ({
      ...prev,
      companyId: value?.id || 0,
      customerTypeId: 0,
      catalogueIds: [],
      catalogueEntries: [],
      orderTypeIds: [],
      orderTypeEntries: [],
    }));
  };

  const handleMarketChange = (event: React.SyntheticEvent, value: Market | null) => {
    setFormData(prev => ({
      ...prev,
      customerTypeId: value?.customerTypeId || 0,
      catalogueIds: [],
      catalogueEntries: [],
      orderTypeIds: [],
      orderTypeEntries: [],
    }));
  };

  const handleCategoryChange = (event: React.SyntheticEvent, value: MarketCategory | null) => {
    setFormData(prev => ({
      ...prev,
      marketPrimaryCategoryId: value?.categoryId || undefined,
      primaryCategoryId: value?.categoryId || undefined,
      marketPrimaryCategoryName: value?.name || '',
      primaryCategoryName: value?.name || '',
    }));
  };

  const handleCatalogueToggle = (catalogueId: number) => {
    setFormData(prev => {
      const isCurrentlySelected = prev.catalogueIds.includes(catalogueId);
      if (isCurrentlySelected) {
        return {
          ...prev,
          catalogueIds: prev.catalogueIds.filter(id => id !== catalogueId),
          catalogueEntries: prev.catalogueEntries.filter(entry => entry.catalogueId !== catalogueId),
        };
      } else {
        return {
          ...prev,
          catalogueIds: [...prev.catalogueIds, catalogueId],
          catalogueEntries: [
            ...prev.catalogueEntries,
            {
              catalogueId,
              startDate: '',
              endDate: '',
            },
          ],
        };
      }
    });
  };

  const handleCatalogueEntryDateChange = (catalogueId: number, field: 'startDate' | 'endDate', value: string) => {
    setFormData(prev => ({
      ...prev,
      catalogueEntries: prev.catalogueEntries.map(entry =>
        entry.catalogueId === catalogueId
          ? { ...entry, [field]: value }
          : entry,
      ),
    }));
  };

  const handleOrderTypeEntryDateChange = (orderTypeId: number, field: 'startDate' | 'endDate', value: string) => {
    setFormData(prev => {
      const existingEntry = prev.orderTypeEntries.find(entry => entry.orderTypeId === orderTypeId);

      if (existingEntry) {
        return {
          ...prev,
          orderTypeEntries: prev.orderTypeEntries.map(entry =>
            entry.orderTypeId === orderTypeId
              ? { ...entry, [field]: value }
              : entry,
          ),
        };
      } else {
        return {
          ...prev,
          orderTypeEntries: [
            ...prev.orderTypeEntries,
            {
              orderTypeId,
              startDate: field === 'startDate' ? value : '',
              endDate: field === 'endDate' ? value : '',
            },
          ],
        };
      }
    });
  };

  const handleAddUom = () => {
    const newUom: MarketItemUom = {
      id: 0,
      uomTypeId: 0,
      uomTypeName: '',
      uomTypeCode: '',
      isOrderableUom: false,
      isPrimaryUom: false,
      isMeasurement: false,
      uomName: '',
      numberOfBaseUnitsOrEaches: 1,
      weight: undefined,
      volume: undefined,
      price: undefined,
    };

    setFormData(prev => ({
      ...prev,
      uoms: [...prev.uoms, newUom],
    }));
  };

  // lazy: no server-side history lookup here, so we always ask before a delete goes through -
  // the confirmation copy covers the historic-order-impact risk generically.
  const handleRemoveUom = (index: number) => {
    setPendingRemoveUomIndex(index);
  };

  const confirmRemoveUom = () => {
    if (pendingRemoveUomIndex !== null) {
      const index = pendingRemoveUomIndex;
      setFormData(prev => ({
        ...prev,
        uoms: prev.uoms.filter((_, idx) => idx !== index),
      }));
    }
    setPendingRemoveUomIndex(null);
  };

  const cancelRemoveUom = () => setPendingRemoveUomIndex(null);

  // Conversions are capped to 2 decimal places everywhere (display, typing, and load) so
  // precision can't silently creep in via cascaded math or data loaded from the API.
  const roundTo2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

  // Base UOM is always the eaches=1 reference; only its weight is persisted, and only the
  // orderable UOM's price is persisted. Everything else recalculates off numberOfBaseUnitsOrEaches.
  const normalizeUoms = (uoms: MarketItemUom[]): MarketItemUom[] => uoms.map((uom) => ({
    ...uom,
    numberOfBaseUnitsOrEaches: uom.isPrimaryUom ? 1 : roundTo2(uom.numberOfBaseUnitsOrEaches),
    weight: uom.isPrimaryUom ? uom.weight : undefined,
    price: uom.isOrderableUom ? uom.price : undefined,
  }));

  const handleUomTypeChange = (index: number, uomTypeId: number) => {
    const selectedUomType = uomTypes.find((ut) => ut.uomTypeId === uomTypeId);
    setFormData(prev => ({
      ...prev,
      uoms: normalizeUoms(prev.uoms.map((uom, idx) => (idx === index ? {
        ...uom,
        uomTypeId,
        uomTypeName: selectedUomType?.uomName || uom.uomTypeName,
        uomTypeCode: selectedUomType?.uomCode || uom.uomTypeCode,
        uomName: selectedUomType?.uomName || uom.uomName,
      } : uom))),
    }));
  };

  // Switching the base UOM is a change of reference frame only - the real-world relationships
  // between every other UOM must stay exactly the same, so rescale each one's eaches by the
  // newly-chosen base's *current* eaches (its conversion under the old base) rather than
  // leaving them expressed against the old base. Without this, the matrix would silently
  // misreport every ratio that doesn't involve the old base.
  const applyBaseUomSelection = (index: number) => {
    setFormData(prev => {
      const scaleFactor = prev.uoms[index]?.numberOfBaseUnitsOrEaches || 1;
      const rescaledUoms = prev.uoms.map((uom, idx) => ({
        ...uom,
        isPrimaryUom: idx === index,
        numberOfBaseUnitsOrEaches: idx === index ? 1 : roundTo2(uom.numberOfBaseUnitsOrEaches / scaleFactor),
      }));
      return {
        ...prev,
        uoms: normalizeUoms(rescaledUoms),
      };
    });
  };

  // Changing the base UOM after one is already set has real consequences (reporting, stock on
  // hand, pricing all key off it), so confirm before applying - unless this is the first pick.
  const handleSelectBaseUom = (index: number) => {
    if (baseUomIndex >= 0 && baseUomIndex !== index) {
      setPendingBaseUomIndex(index);
      return;
    }
    applyBaseUomSelection(index);
  };

  const confirmBaseUomChange = () => {
    if (pendingBaseUomIndex !== null) {
      applyBaseUomSelection(pendingBaseUomIndex);
    }
    setPendingBaseUomIndex(null);
  };

  const cancelBaseUomChange = () => setPendingBaseUomIndex(null);

  const applyOrderableUomSelection = (index: number) => {
    setFormData(prev => ({
      ...prev,
      uoms: normalizeUoms(prev.uoms.map((uom, idx) => ({
        ...uom,
        isOrderableUom: idx === index,
      }))),
    }));
  };

  // Changing the orderable UOM after one is already set affects ordering, receiving and pricing
  // for the item going forward, so confirm before applying - unless this is the first pick.
  const handleSelectOrderableUom = (index: number) => {
    if (orderableUomIndex >= 0 && orderableUomIndex !== index) {
      setPendingOrderableUomIndex(index);
      return;
    }
    applyOrderableUomSelection(index);
  };

  const confirmOrderableUomChange = () => {
    if (pendingOrderableUomIndex !== null) {
      applyOrderableUomSelection(pendingOrderableUomIndex);
    }
    setPendingOrderableUomIndex(null);
  };

  const cancelOrderableUomChange = () => setPendingOrderableUomIndex(null);

  const handleBaseWeightChange = (value: number | undefined) => {
    setFormData(prev => ({
      ...prev,
      uoms: prev.uoms.map((uom) => (uom.isPrimaryUom ? { ...uom, weight: value } : uom)),
    }));
  };

  const handleOrderablePriceChange = (value: number | undefined) => {
    setFormData(prev => ({
      ...prev,
      uoms: prev.uoms.map((uom) => (uom.isOrderableUom ? { ...uom, price: value } : uom)),
    }));
  };

  // Matrix cell (row, col) = quantity of the column UOM equal to one row UOM, derived from each
  // UOM's numberOfBaseUnitsOrEaches (its conversion factor into the base/eaches unit). Editing a
  // cell solves the row's eaches back out from the target column's eaches, so every other cell in
  // the matrix (which is just a ratio of two eaches values) cascades/recalculates on render - this
  // is a pure read derived from state on every render, not a chained state update, so there's no
  // risk of an update loop even when a cascade touches every other displayed cell.
  const getMatrixValue = (rowIndex: number, colIndex: number): number | undefined => {
    if (rowIndex === colIndex) return 1;
    const rowEaches = formData.uoms[rowIndex]?.numberOfBaseUnitsOrEaches;
    const colEaches = formData.uoms[colIndex]?.numberOfBaseUnitsOrEaches;
    if (!rowEaches || !colEaches) return undefined;
    return roundTo2(rowEaches / colEaches);
  };

  // Single setFormData call per edit (no effects watch formData.uoms), so a cascade can only ever
  // run once per user action - there's no path for it to re-trigger itself or loop.
  const handleMatrixChange = (rowIndex: number, colIndex: number, value: number) => {
    if (rowIndex === colIndex || Number.isNaN(value) || value === 0) return;
    const roundedValue = roundTo2(value);
    setFormData(prev => {
      const rowUom = prev.uoms[rowIndex];
      const rowEaches = rowUom?.numberOfBaseUnitsOrEaches || 0;
      const colEaches = prev.uoms[colIndex]?.numberOfBaseUnitsOrEaches || 0;

      // The base row's own eaches is always fixed at 1, so editing a cell on that row solves the
      // *column* UOM's eaches instead - the ratio still cascades through every other cell.
      if (rowUom?.isPrimaryUom) {
        const newColEaches = roundTo2(rowEaches / roundedValue);
        return {
          ...prev,
          uoms: normalizeUoms(prev.uoms.map((uom, idx) => (idx === colIndex ? { ...uom, numberOfBaseUnitsOrEaches: newColEaches } : uom))),
        };
      }

      const newRowEaches = roundTo2(roundedValue * colEaches);
      return {
        ...prev,
        uoms: normalizeUoms(prev.uoms.map((uom, idx) => (idx === rowIndex ? { ...uom, numberOfBaseUnitsOrEaches: newRowEaches } : uom))),
      };
    });
  };

  const getUomLabel = (uom: MarketItemUom): string => {
    const baseName = uom.uomTypeName || uom.uomName || t('uomType');
    let label = baseName;
    if (uom.isPrimaryUom) label += ` (${t('base', 'Base')})`;
    if (uom.isOrderableUom) label += ` (${t('order', 'Order')})`;
    return label;
  };

  const handleCancel = () => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);
    if (listSort) params.set('sort', listSort);
    if (listDirection) params.set('direction', listDirection);
    if (listPage) params.set('page', listPage);
    const queryString = params.toString();
    navigate(`/procurement/products${queryString ? '?' + queryString : ''}`);
  };

  const validateForm = (): boolean => {
    if (!formData.itemNumber || !formData.itemName) {
      setErrorMessage(t('validationRequired'));
      setShowErrorMessage(true);
      return false;
    }

    const primaryUoms = formData.uoms.filter(u => u.isPrimaryUom);
    const orderableUoms = formData.uoms.filter(u => u.isOrderableUom);

    if (primaryUoms.length !== 1) {
      setErrorMessage(t('atLeastOnePrimaryUom'));
      setShowErrorMessage(true);
      return false;
    }

    if (orderableUoms.length !== 1) {
      setErrorMessage(t('atLeastOneOrderableUom'));
      setShowErrorMessage(true);
      return false;
    }

    // Mirror the bulk-upload mandatory-field checks (ItemAllergens/ItemNutrition sheets) so data
    // saved via the UI can always round-trip through download/upload without hitting validation
    // errors there. AllergenRecordId/NutritionRecordId are auto-generated server-side, so they're
    // not required here.
    const hasBlankAllergenFields = (formData.allergens || []).some((a) => (
      !a.allergenCode?.trim() || !a.allergenStatus?.trim()
    ));
    if (hasBlankAllergenFields) {
      setErrorMessage(t('validationAllergensRequired', 'All allergens must have a Code and Status.'));
      setShowErrorMessage(true);
      return false;
    }

    const hasBlankNutritionFields = (formData.nutrition || []).some((n) => (
      !n.nutritionProfileId?.trim() || !n.nutrientCode?.trim() || n.nutrientValue === undefined || n.nutrientValue === null ||
      !n.valueUom?.trim() || !n.basisType?.trim() || n.basisQuantity === undefined || n.basisQuantity === null || !n.basisUom?.trim()
    ));
    if (hasBlankNutritionFields) {
      setErrorMessage(t('validationNutritionRequired', 'All nutrition rows must have Profile Id, Nutrient Code, Value, Unit, Basis Type, Basis Quantity and Basis Unit.'));
      setShowErrorMessage(true);
      return false;
    }

    const hasBlankDietaryAttributeFields = (formData.dietaryAttributes || []).some((d) => (
      !d.dietaryAttributeCode?.trim() || !d.dietaryAttributeStatus?.trim()
    ));
    if (hasBlankDietaryAttributeFields) {
      setErrorMessage(t('validationDietaryAttributesRequired', 'All dietary attributes must have a Code and Status.'));
      setShowErrorMessage(true);
      return false;
    }

    // Guard against invalid single-ended dates (e.g. a partially-typed calendar value) slipping
    // through to submission - convertToJsonNetDate would otherwise turn these into "/Date(NaN)/"
    // and fail WCF deserialization, blocking the whole product save.
    const hasInvalidAllergenDate = (formData.allergens || []).some((a) => (
      (a.effectiveFrom !== undefined && !dayjs(a.effectiveFrom).isValid()) ||
      (a.effectiveTo !== undefined && !dayjs(a.effectiveTo).isValid())
    ));
    if (hasInvalidAllergenDate) {
      setErrorMessage(t('validationAllergenDateInvalid', 'Allergen Effective From/To dates must be valid or blank.'));
      setShowErrorMessage(true);
      return false;
    }

    const hasInvalidNutritionDate = (formData.nutrition || []).some((n) => (
      (n.effectiveFrom !== undefined && !dayjs(n.effectiveFrom).isValid()) ||
      (n.effectiveTo !== undefined && !dayjs(n.effectiveTo).isValid())
    ));
    if (hasInvalidNutritionDate) {
      setErrorMessage(t('validationNutritionDateInvalid', 'Nutrition Effective From/To dates must be valid or blank.'));
      setShowErrorMessage(true);
      return false;
    }

    const hasInvalidDietaryAttributeDate = (formData.dietaryAttributes || []).some((d) => (
      (d.effectiveFrom !== undefined && !dayjs(d.effectiveFrom).isValid()) ||
      (d.effectiveTo !== undefined && !dayjs(d.effectiveTo).isValid())
    ));
    if (hasInvalidDietaryAttributeDate) {
      setErrorMessage(t('validationDietaryAttributeDateInvalid', 'Dietary Attribute Effective From/To dates must be valid or blank.'));
      setShowErrorMessage(true);
      return false;
    }

    // Mirror the bulk-upload date-range validation (EffectiveTo must not precede EffectiveFrom)
    // so allergen/nutrition periods created via the UI are just as valid as those uploaded via Excel.
    const hasInvalidAllergenDateRange = (formData.allergens || []).some((a) => (
      a.effectiveFrom && a.effectiveTo && dayjs(a.effectiveTo).isBefore(dayjs(a.effectiveFrom))
    ));
    if (hasInvalidAllergenDateRange) {
      setErrorMessage(t('validationAllergenDateRange', 'Allergen Effective To date cannot be before Effective From date.'));
      setShowErrorMessage(true);
      return false;
    }

    const hasInvalidNutritionDateRange = (formData.nutrition || []).some((n) => (
      n.effectiveFrom && n.effectiveTo && dayjs(n.effectiveTo).isBefore(dayjs(n.effectiveFrom))
    ));
    if (hasInvalidNutritionDateRange) {
      setErrorMessage(t('validationNutritionDateRange', 'Nutrition Effective To date cannot be before Effective From date.'));
      setShowErrorMessage(true);
      return false;
    }

    const hasInvalidDietaryAttributeDateRange = (formData.dietaryAttributes || []).some((d) => (
      d.effectiveFrom && d.effectiveTo && dayjs(d.effectiveTo).isBefore(dayjs(d.effectiveFrom))
    ));
    if (hasInvalidDietaryAttributeDateRange) {
      setErrorMessage(t('validationDietaryAttributeDateRange', 'Dietary Attribute Effective To date cannot be before Effective From date.'));
      setShowErrorMessage(true);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);

    try {

      const primaryUom = formData.uoms.find(u => u.isPrimaryUom);
      const orderableUom = formData.uoms.find(u => u.isOrderableUom);

      const updatedFormData = {
        ...formData,
        primaryUom: primaryUom?.uomName || '',
        orderableUom: orderableUom?.uomName || '',
        storeNumber: selectedStore?.storeNumber || '',
        allergens: (formData.allergens || []).map((a) => ({
          ...a,
          effectiveFrom: convertToJsonNetDate(a.effectiveFrom || null) || undefined,
          effectiveTo: convertToJsonNetDate(a.effectiveTo || null) || undefined,
        })),
        nutrition: (formData.nutrition || []).map((n) => ({
          ...n,
          effectiveFrom: convertToJsonNetDate(n.effectiveFrom || null) || undefined,
          effectiveTo: convertToJsonNetDate(n.effectiveTo || null) || undefined,
        })),
        dietaryAttributes: (formData.dietaryAttributes || []).map((d) => ({
          ...d,
          effectiveFrom: convertToJsonNetDate(d.effectiveFrom || null) || undefined,
          effectiveTo: convertToJsonNetDate(d.effectiveTo || null) || undefined,
        })),
      };

      if (isEditMode && id) {
        const response = await client.updateMarketItem(id, updatedFormData);
        if (response.success) {
          setShowSuccessMessage(true);
        } else {
          setErrorMessage(response.message || t('errorMessage'));
          setShowErrorMessage(true);
        }
      } else {
        const response = await client.createMarketItem(updatedFormData);
        if (response.success) {
          setShowSuccessMessage(true);
          if (response.data) {
            const params = new URLSearchParams();
            if (companyId) params.set('companyId', companyId);
            if (customerTypeId) params.set('customerTypeId', customerTypeId);
            if (searchTerm) params.set('searchTerm', searchTerm);
            navigate(`/procurement/products/edit/${response.data}?${params.toString()}`);
          }
        } else if (response.message) {
          setErrorMessage(response.message);
          setShowErrorMessage(true);
        } else {
          setErrorMessage(t('errorMessage'));
          setShowErrorMessage(true);
        }
      }
    } catch (error) {
      const errorMsg = extractErrorMessage(error);
      logError('Failed to save product');
      setErrorMessage(errorMsg || t('errorMessage'));
      setShowErrorMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DetailPageTemplate
      title={isEditMode ? t('editProduct') : t('createProduct')}
      subtitle={t('productFormSubtitle', 'Manage product details, catalogue assignment, and units of measure.')}
      loading={isLoading}
      loadingLabel={t('loading')}
      actions={(
        <>
          <Button
            variant="secondary"
            size="lg"
            onClick={handleCancel}
            disabled={isSaving}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? t('saving') : (isEditMode ? t('update') : t('create'))}
          </Button>
        </>
      )}
      headlineContent={{
        picture: (
          <PictureTile
            src={formData.imageUrl}
            alt={imageTileTitle || t('itemNumber')}
            objectFit="cover"
            fallbackLabel={t('noImageAvailable', 'No image available')}
          />
        ),
        insights: [
          <InsightTile
            key="active"
            label={t('activeStatus', 'Active Status')}
            value={isActive ? t('active', 'Active') : t('inactive', 'Inactive')}
            description={isActive
              ? t('activeStatusRuleShort', 'Meets date, catalogue, price, and UOM checks.')
              : inactiveReason}
            icon={isActive ? <CheckCircle /> : <Cancel />}
            variant={isActive ? 'success' : 'error'}
          />,
          <InsightTile
            key="inventory"
            label={t('uomSummaryBaseUnit', 'Base Unit / Weight')}
            value={primaryUom?.uomName || t('notSet', 'Not set')}
            description={`${t('weight', 'Weight')}: ${primaryUom?.weight != null ? primaryUom.weight : t('notSet', 'Not set')}`}
            icon={<Inventory2 />}
            variant="primary"
          />,
          <InsightTile
            key="ordering"
            label={t('ordering', 'Ordering')}
            value={hasCostPrice ? applyRoundAndFormat(Number(pricedUom?.price || 0), marketCurrencyCode) : t('notSet', 'Not set')}
            description={hasOrderableUom
              ? `${t('orderableUnitLabel', 'Orderable Unit')}: ${orderableUom?.uomName || t('uom', 'UOM')}`
              : t('addUomPricePrompt', 'Add a UOM price to activate the item.')}
            icon={<AttachMoney />}
            variant="success"
          />,
          <InsightTile
            key="catalogues"
            label={t('catalogueCount', 'Active Catalogues')}
            value={`${catalogueTileActiveCount} / ${catalogueTileAssignedCount}`}
            description={t('cataloguesActiveOfAssigned', 'Active of assigned catalogues')}
            icon={<MenuBook />}
            variant="warning"
          />,
          <InsightTile
            key="allergens"
            label={t('allergens', 'Allergens')}
            value={containsAllergenCount > 0
              ? `${containsAllergenCount} ${t('allergenStatusContains', 'Contains')}`
              : t('notSet', 'Not set')}
            description={`${(formData.allergens || []).length} ${t('allergensCaptured', 'captured')}`}
            icon={<ReportProblem />}
            variant="warning"
          />,
          <InsightTile
            key="suppliers"
            label={t('supplierOverrides', 'Supplier Overrides')}
            value={`${overriddenSupplierCount} / ${marketSuppliers.length}`}
            description={t('overriddenOfLinkedSuppliers', 'Active overrides of linked suppliers')}
            icon={<LocalShipping />}
            variant="secondary"
          />,
        ],
      }}
    >
      <Tabs
        value={tabValue}
        onChange={(_event, newValue) => setTabValue(newValue)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mt: 6,
          mb: '-1px',
          ml: 4,
          mr: 4,
          px: 2,
        }}
        variant="scrollable"
        scrollButtons={false}
        allowScrollButtonsMobile={false}
      >
        <Tab label={t('overview', 'Overview')} />
        <Tab label={t('catalogues', 'Catalogues')} />
        <Tab label={t('ordering', 'Ordering')} />
        <Tab label={t('inventory', 'Inventory')} />
        <Tab label={t('packagingAndUnits', 'Packaging & Units')} />
        <Tab label={t('storeOverrides', 'Store Overrides')} />
        <Tab label={t('supplierOverrides', 'Supplier Overrides')} />
        <Tab label={t('allergens', 'Allergens')} />
        {isDemoMode && (
          <Tab label={t('ingredients', 'Ingredients')} />
        )}
        <Tab label={t('nutritionalInfo', 'Nutritional Info')} />
        <Tab label={t('dietaryAttributesInfo', 'Dietary Attributes')} />
      </Tabs>

      <Section style={{ mt: 0, p: 5, pb: 0 }}>
        <Box sx={{ p: { xs: 4, md: 5 } }}>
          <Grid container
            spacing={8}>
            {/* Basic Information */}
            <Grid item
              xs={12}
              sx={{ display: tabValue === 0 ? 'block' : 'none' }}>
              <Box sx={{ p: 4 }}>
                <Grid container
                  spacing={4}>
                  <Grid item
                    xs={12}
                    md={6}>
                    <Autocomplete
                      options={instances}
                      getOptionLabel={(option) => option.name}
                      value={instances.find(i => i.id === formData.companyId) || null}
                      onChange={handleInstanceChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('instance')}
                          required
                        />
                      )}
                      disabled={isEditMode}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <Autocomplete
                      options={markets}
                      getOptionLabel={(option) => option.name}
                      value={markets.find(m => m.customerTypeId === formData.customerTypeId) || null}
                      onChange={handleMarketChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('market')}
                          required
                        />
                      )}
                      disabled={isEditMode || !formData.companyId}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('itemNumber')}
                      value={formData.itemNumber}
                      onChange={handleInputChange('itemNumber')}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('erpInternalId')}
                      value={formData.erpInternalId || ''}
                      onChange={handleInputChange('erpInternalId')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('itemName')}
                      value={formData.itemName}
                      onChange={handleInputChange('itemName')}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('itemFriendlyName')}
                      value={formData.itemFriendlyName}
                      onChange={handleInputChange('itemFriendlyName')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <FormControl fullWidth>
                      <TextField
                        select
                        label={t('productType')}
                        value={formData.productType || ''}
                        onChange={handleInputChange('productType')}
                        fullWidth
                      >
                        <MenuItem value="">
                          <em>{t('selectProductType')}</em>
                        </MenuItem>
                        <MenuItem value="FOOD">FOOD</MenuItem>
                        <MenuItem value="PAPER">PAPER</MenuItem>
                        <MenuItem value="EXPENSE">EXPENSE</MenuItem>
                        <MenuItem value="OTHER">OTHER</MenuItem>
                      </TextField>
                    </FormControl>
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <Autocomplete
                      options={categories}
                      getOptionLabel={(option) => option.name}
                      value={categories.find(c => c.categoryId === formData.marketPrimaryCategoryId) || null}
                      onChange={handleCategoryChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('primaryCategory')}
                        />
                      )}
                      disabled={!formData.companyId}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={12}>
                    <TextField
                      label={t('imageUrl')}
                      value={formData.imageUrl ?? ''}
                      onChange={handleInputChange('imageUrl')}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Inventory Details */}
            <Grid item
              xs={12}
              sx={{ display: tabValue === 3 ? 'block' : 'none' }}>
              <Box sx={{ p: 4 }}>
                <Typography
                  variant="textSM"
                  component="div"
                  color="text.secondary"
                  sx={{ mb: 6 }}
                >
                  {t(
                    'inventoryTabIntro',
                    'Inventory items group one or more supplier products into a single item for inventory tracking. Stores and retailers can order the supplier products assigned to them, but once receipted, they are converted into the linked inventory item. This keeps stock counts, reporting and inventory activity consistent even when products are supplied by different suppliers or supplier products change over time.',
                  )}
                </Typography>
                <Grid container
                  spacing={4}>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={useInventorySKU ? t('inventorySKU') : t('marketItemNumber')}
                      value={formData.marketItemNumber}
                      onChange={handleInputChange('marketItemNumber')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('marketItemName')}
                      value={formData.marketItemName}
                      onChange={handleInputChange('marketItemName')}
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <LinkedItems
                  companyId={formData.companyId}
                  customerTypeId={formData.customerTypeId}
                  contentId={formData.contentId}
                  marketItemNumber={formData.marketItemNumber}
                  itemNumber={formData.itemNumber}
                  markets={markets}
                  currentUoms={formData.uoms}
                />
              </Box>
            </Grid>

            {/* Orderable Unit of Measure */}
            <Grid item
              xs={12}
              sx={{ display: tabValue === 2 ? 'block' : 'none' }}>
              <Box sx={{ p: 4 }}>
                <Grid container
                  spacing={4}>
                  <Grid item
                    xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="textSM"
                        sx={{ fontWeight: 600 }}
                      >
                        {t('orderableUnitOfMeasure', 'Orderable unit')}
                      </Typography>
                      <Tooltip
                        title={t(
                          'orderableUnitTooltip',
                          'The unit stores or retailers can order this product in, such as case, pack, bag or each.',
                        )}
                      >
                        <InfoOutlined
                          fontSize="small"
                          sx={{ color: 'text.secondary' }}
                        />
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="textSM"
                      color="text.secondary"
                    >
                      {t(
                        'orderableUnitExplanation',
                        'The unit available for stores or retailers to order.',
                      )}
                    </Typography>
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      select
                      label={t('orderableUnit', 'Orderable Unit')}
                      value={orderableUomIndex >= 0 ? orderableUomIndex : ''}
                      onChange={(e) => handleSelectOrderableUom(Number(e.target.value))}
                      fullWidth
                      sx={{ mb: 0 }}
                    >
                      {formData.uoms.map((uom, index) => (
                        <MenuItem key={index}
                          value={index}>
                          {uom.uomTypeName || uom.uomName || t('uomType')}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('price')}
                      type="number"
                      fullWidth
                      sx={{ mb: 0 }}
                      disabled={orderableUomIndex < 0}
                      value={orderableUomIndex >= 0 ? (formData.uoms[orderableUomIndex].price ?? '') : ''}
                      onChange={(e) => handleOrderablePriceChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Valid Period */}
            <Grid item
              xs={12}
              sx={{ display: tabValue === 2 ? 'block' : 'none' }}>
              <Box sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 6 }}>
                  <Typography
                    variant="textSM"
                    sx={{ fontWeight: 600 }}
                  >
                    {t('validityPeriod')}
                  </Typography>
                </Box>
                <Grid container
                  spacing={4}>
                  <Grid item
                    xs={12}
                    md={6}>
                    <DatePicker
                      label={t('startDate')}
                      value={toDateValue(formData.startDate)}
                      onChange={(newValue) => {
                        const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                        setFormData(prev => ({ ...prev, startDate: formatted }));
                      }}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <DatePicker
                      label={t('endDate')}
                      value={toDateValue(formData.endDate)}
                      onChange={(newValue) => {
                        const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                        setFormData(prev => ({ ...prev, endDate: formatted }));
                      }}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Quantity Limits */}
            <Grid item
              xs={12}
              sx={{ display: tabValue === 2 ? 'block' : 'none' }}>
              <Box sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 6 }}>
                  <Typography
                    variant="textSM"
                    sx={{ fontWeight: 600 }}
                  >
                    {t('quantityLimits')}
                  </Typography>
                </Box>
                <Grid container
                  spacing={4}>
                  <Grid item
                    xs={12}
                    md={4}>
                    <TextField
                      label={t('minQuantity')}
                      type="number"
                      value={formData.minQuantity || ''}
                      onChange={handleNumberInputChange('minQuantity')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={4}>
                    <DatePicker
                      label={t('minQuantityStartDate')}
                      value={toDateValue(formData.minQuantityStartDate)}
                      onChange={(newValue) => {
                        const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                        setFormData(prev => ({ ...prev, minQuantityStartDate: formatted }));
                      }}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={4}>
                    <DatePicker
                      label={t('minQuantityEndDate')}
                      value={toDateValue(formData.minQuantityEndDate)}
                      onChange={(newValue) => {
                        const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                        setFormData(prev => ({ ...prev, minQuantityEndDate: formatted }));
                      }}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={4}>
                    <TextField
                      label={t('maxQuantity')}
                      type="number"
                      value={formData.maxQuantity || ''}
                      onChange={handleNumberInputChange('maxQuantity')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={4}>
                    <DatePicker
                      label={t('maxQuantityStartDate')}
                      value={toDateValue(formData.maxQuantityStartDate)}
                      onChange={(newValue) => {
                        const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                        setFormData(prev => ({ ...prev, maxQuantityStartDate: formatted }));
                      }}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={4}>
                    <DatePicker
                      label={t('maxQuantityEndDate')}
                      value={toDateValue(formData.maxQuantityEndDate)}
                      onChange={(newValue) => {
                        const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                        setFormData(prev => ({ ...prev, maxQuantityEndDate: formatted }));
                      }}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  {/* PAR Level — only visible when the selected market has PAR ordering enabled */}
                  {isParEnabled && (
                    <Grid item
                      xs={12}
                      md={4}>
                      <TextField
                        label={t('parLevel')}
                        type="number"
                        inputProps={{ min: 1 }}
                        value={formData.parLevel ?? ''}
                        onChange={handleNumberInputChange('parLevel')}
                        fullWidth
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Grid>

            {/* Catalogue Assignment and Order Channel Overrides */}
            <Grid item
              xs={12}
              sx={{ display: tabValue === 1 ? 'block' : 'none' }}>
              <Box sx={{ p: 4 }}>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Box
                    sx={{
                      p: 5,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', md: 'center' },
                      gap: 3,
                      flexWrap: 'wrap',
                      width: '100%',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography
                        variant="textMD"
                        sx={{ fontWeight: 600 }}
                      >
                        {t('catalogues', 'Catalogues')}
                      </Typography>
                      {sortedFilteredCatalogueRows.length > 0 && (
                        <Pill text={`${sortedFilteredCatalogueRows.length} ${t('catalogues', 'Catalogues')}`} />
                      )}
                    </Box>
                    <TextField
                      margin="none"
                      size="small"
                      placeholder={t('searchCatalogues', 'Search catalogues or order channels')}
                      value={catalogueSearch}
                      onChange={(event) => setCatalogueSearch(event.target.value)}
                      sx={{ width: { xs: '100%', md: 360 }, mb: 0 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search
                              fontSize="small"
                              color="inherit"
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <TableContainer>
                    <Table
                      sx={{
                        minWidth: '1200px',
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('catalogues')}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('orderChannels', 'Order Channels')}</TableCell>
                          <TableCell align="center">{t('assignedCount', 'Assigned')}</TableCell>
                          <TableCell align="center"
                            sx={{ whiteSpace: 'nowrap' }}>{t('startDate', 'Start Date')}</TableCell>
                          <TableCell align="center"
                            sx={{ whiteSpace: 'nowrap' }}>{t('endDate', 'End Date')}</TableCell>
                          <TableCell align="center"
                            sx={{ whiteSpace: 'nowrap' }}>{t('status', 'Status')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedCatalogueRows.map(({ orderTypeNames, catalogue }) => {
                          const isSelected = formData.catalogueIds.includes(catalogue.customerGroupId);
                          const catalogueEntry = formData.catalogueEntries.find(
                            (entry) => entry.catalogueId === catalogue.customerGroupId,
                          );
                          const status = getCatalogueStatus(
                            isSelected,
                            catalogueEntry?.startDate,
                            catalogueEntry?.endDate,
                            catalogue.customerGroupId,
                          );

                          return (
                            <TableRow key={catalogue.customerGroupId}>
                              <TableCell>{catalogue.name}</TableCell>
                              <TableCell>{orderTypeNames.join(', ') || '-'}</TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => handleCatalogueToggle(catalogue.customerGroupId)}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <DatePicker
                                  value={toDateValue(catalogueEntry?.startDate)}
                                  disabled={!isSelected}
                                  onChange={(newValue) => {
                                    const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                                    handleCatalogueEntryDateChange(
                                      catalogue.customerGroupId,
                                      'startDate',
                                      formatted,
                                    );
                                  }}
                                  slotProps={{ textField: { size: 'small', margin: 'none', sx: { mb: 0 } } }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <DatePicker
                                  value={toDateValue(catalogueEntry?.endDate)}
                                  disabled={!isSelected}
                                  onChange={(newValue) => {
                                    const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                                    handleCatalogueEntryDateChange(
                                      catalogue.customerGroupId,
                                      'endDate',
                                      formatted,
                                    );
                                  }}
                                  slotProps={{ textField: { size: 'small', margin: 'none', sx: { mb: 0 } } }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                {status && (
                                  <Box
                                    component="span"
                                    sx={{
                                      display: 'inline-flex',
                                      px: 2,
                                      py: 0.5,
                                      borderRadius: 999,
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      textTransform: 'capitalize',
                                      backgroundColor: status === 'current'
                                        ? '#2e7d3220'
                                        : status === 'overridden'
                                          ? '#d32f2f20'
                                          : status === 'future'
                                            ? '#ed6c0220'
                                            : '#d32f2f20',
                                      color: status === 'current'
                                        ? '#2e7d32'
                                        : status === 'overridden'
                                          ? '#d32f2f'
                                          : status === 'future'
                                            ? '#ed6c02'
                                            : '#d32f2f',
                                    }}
                                  >
                                    {status === 'current'
                                      ? t('current', 'Current')
                                      : status === 'overridden'
                                        ? t('overridden', 'Overridden')
                                        : status === 'future'
                                          ? t('future', 'Future')
                                          : t('expired', 'Expired')}
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {pagedCatalogueRows.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              align="center"
                            >
                              <Typography
                                variant="textSM"
                                color="text.secondary"
                              >
                                {t('noCatalogueMatches', 'No catalogues match your search.')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box
                    sx={{
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Pagination
                      page={currentCataloguePage}
                      setPage={setCataloguePage}
                      maxPages={catalogueTotalPages}
                      breakpointForChangeDisplay={120000}
                      useNoDataMessage={false}
                    />
                  </Box>
                </Box>

                {formData.catalogueIds.length > 0 && (
                  <Box sx={{ mt: 6 }}>
                    <Typography
                      variant="textLG"
                      sx={{ mb: 8, fontWeight: 600 }}
                    >
                      {t('orderChannelOverrides', 'Order Channel Overrides')}
                    </Typography>

                    {configuredOrderTypeOverrides.length > 0 ? (
                      <Stack spacing={3}>
                        {configuredOrderTypeOverrides.map(({ orderType, entry }) => (
                          <Box
                            key={orderType.orderTypeId}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', md: '1fr auto auto' },
                              gap: 2,
                              alignItems: 'center',
                              p: 3,
                            }}
                          >
                            <Typography
                              variant="textSM"
                              sx={{ fontWeight: 600 }}
                            >
                              {orderType.name}
                            </Typography>
                            <DatePicker
                              label={t('startDate', 'Start Date')}
                              value={toDateValue(entry?.startDate)}
                              onChange={(newValue) => {
                                const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                                handleOrderTypeEntryDateChange(orderType.orderTypeId, 'startDate', formatted);
                              }}
                              slotProps={{ textField: { size: 'small', margin: 'none', sx: { mb: 0 } } }}
                            />
                            <DatePicker
                              label={t('endDate', 'End Date')}
                              value={toDateValue(entry?.endDate)}
                              onChange={(newValue) => {
                                const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                                handleOrderTypeEntryDateChange(orderType.orderTypeId, 'endDate', formatted);
                              }}
                              slotProps={{ textField: { size: 'small', margin: 'none', sx: { mb: 0 } } }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography
                        variant="textSM"
                        color="text.secondary"
                      >
                        {t('noOrderChannelOverrides', 'No order channel overrides configured.')}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>

            {/* UOM Configuration */}
            <Grid item
              xs={12}
              sx={{ display: tabValue === 4 ? 'block' : 'none' }}>
              <Box sx={{ p: 4 }}>
                <Typography
                  variant="textLG"
                  component="div"
                  sx={{ mb: 6, fontWeight: 600 }}
                >
                  {t('packagingAndLogistics', 'Packaging & Logistics')}
                </Typography>
                <Grid container
                  spacing={4}
                  sx={{ mb: 6 }}>
                  <Grid item
                    xs={12}
                    md={4}>
                    <TextField
                      label={t('packSize')}
                      type="number"
                      value={formData.packSize || ''}
                      onChange={handleNumberInputChange('packSize')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={4}>
                    <TextField
                      label={t('caseSize')}
                      type="number"
                      value={formData.caseSize || ''}
                      onChange={handleNumberInputChange('caseSize')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={4}>
                    <TextField
                      label={t('innerPackSize')}
                      type="number"
                      value={formData.innerPackSize || ''}
                      onChange={handleNumberInputChange('innerPackSize')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('weight')}
                      type="number"
                      value={formData.weight || ''}
                      onChange={handleNumberInputChange('weight')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('volume')}
                      type="number"
                      value={formData.volume || ''}
                      onChange={handleNumberInputChange('volume')}
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Box display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={6}>
                  <Typography
                    variant="textLG"
                    sx={{ fontWeight: 600 }}
                  >
                    {t('unitsAndConversions', 'Units and Conversions')}
                  </Typography>
                </Box>

                <Grid container
                  spacing={4}
                  sx={{ mb: 6 }}>
                  <Grid item
                    xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="textSM"
                        sx={{ fontWeight: 600 }}
                      >
                        {t('baseUnitOfMeasure', 'Base unit of measure')}
                      </Typography>
                      <Tooltip
                        title={t(
                          'baseUnitTooltip',
                          'The standard unit used to track this product in inventory. Choose the lowest consistent unit that will not change if suppliers or pack sizes change, such as KG, G, L or ML. Use Each only when the item is always a fixed, standard unit, such as buns or cups.',
                        )}
                      >
                        <InfoOutlined
                          fontSize="small"
                          sx={{ color: 'text.secondary' }}
                        />
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="textSM"
                      color="text.secondary"
                    >
                      {t(
                        'baseUnitExplanation',
                        'Used for inventory tracking and conversions. Choose a stable unit such as weight or volume where pack sizes may change.',
                      )}
                    </Typography>
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      select
                      label={t('baseUnit', 'Base Unit')}
                      value={baseUomIndex >= 0 ? baseUomIndex : ''}
                      onChange={(e) => handleSelectBaseUom(Number(e.target.value))}
                      fullWidth
                      sx={{ mb: 0 }}
                    >
                      {formData.uoms.map((uom, index) => (
                        <MenuItem key={index}
                          value={index}>
                          {uom.uomTypeName || uom.uomName || t('uomType')}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item
                    xs={12}
                    md={6}>
                    <TextField
                      label={t('weight')}
                      type="number"
                      fullWidth
                      sx={{ mb: 0 }}
                      disabled={baseUomIndex < 0}
                      value={baseUomIndex >= 0 ? (formData.uoms[baseUomIndex].weight ?? '') : ''}
                      onChange={(e) => handleBaseWeightChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </Grid>
                </Grid>

                <Section>
                  <Box
                    sx={{
                      p: 5,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="textMD"
                        fontWeight={600}
                      >
                        {t('conversions', 'Conversions')}
                      </Typography>
                      {formData.uoms.length > 0 && (
                        <Pill text={`${formData.uoms.length} ${t('conversions', 'Conversions')}`} />
                      )}
                    </Box>
                    <Button
                      variant="secondary"
                      size="lg"
                      startIcon={<SvgIcon><Add fill='black' /></SvgIcon>}
                      onClick={handleAddUom}
                    >
                      {t('addUom')}
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('uomType')}</TableCell>
                          {formData.uoms.map((colUom, colIndex) => (
                            <TableCell key={colIndex}
                              align="center">
                              {getUomLabel(colUom)}
                            </TableCell>
                          ))}
                          <TableCell align="right">{t('actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedUomRows.map(({ uom: rowUom, idx: rowIndex }) => {
                          const isRowLocked = rowUom.isPrimaryUom || rowUom.isOrderableUom;
                          return (
                            <TableRow key={rowIndex}>
                              <TableCell variant="head"
                                sx={{ minWidth: 180 }}>
                                {isRowLocked ? (
                                  <Typography
                                    variant="textSM"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {getUomLabel(rowUom)}
                                  </Typography>
                                ) : (
                                  <FormControl fullWidth>
                                    <Select
                                      value={rowUom.uomTypeId}
                                      onChange={(e) => handleUomTypeChange(rowIndex, Number(e.target.value))}
                                      renderValue={() => getUomLabel(rowUom)}
                                      sx={{ mb: 0, backgroundColor: 'background.paper' }}
                                    >
                                      {uomTypes.map((uomType) => (
                                        <MenuItem key={uomType.uomTypeId}
                                          value={uomType.uomTypeId}>
                                          {uomType.uomName}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                              </TableCell>
                              {formData.uoms.map((colUom, colIndex) => {
                                const isDiagonal = rowIndex === colIndex;
                                const value = getMatrixValue(rowIndex, colIndex);
                                const cellKey = `${rowIndex}-${colIndex}`;
                                const editingValue = matrixEditValues[cellKey];
                                const displayValue = editingValue !== undefined
                                  ? editingValue
                                  : (value !== undefined ? value.toFixed(2) : '');
                                return (
                                  <TableCell key={colIndex}
                                    align="right">
                                    {isDiagonal ? (
                                      <Typography variant="textSM">
                                        {`${(1).toFixed(2)} ${colUom.uomTypeCode || colUom.uomTypeName}`}
                                      </Typography>
                                    ) : (
                                      <TextField
                                        type="number"
                                        size="small"
                                        value={displayValue}
                                        onChange={(e) => {
                                          const inputValue = e.target.value;
                                          setMatrixEditValues((prev) => ({ ...prev, [cellKey]: inputValue }));
                                        }}
                                        onBlur={() => {
                                          setMatrixEditValues((prev) => {
                                            if (prev[cellKey] === undefined) return prev;
                                            const next = { ...prev };
                                            delete next[cellKey];
                                            return next;
                                          });
                                          if (editingValue !== undefined && editingValue !== '') {
                                            handleMatrixChange(rowIndex, colIndex, Number(editingValue));
                                          }
                                        }}
                                        InputProps={{
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              {colUom.uomTypeCode || colUom.uomTypeName}
                                            </InputAdornment>
                                          ),
                                        }}
                                        inputProps={{ step: 0.01, style: { textAlign: 'right' } }}
                                        sx={{ width: 130, mb: 0 }}
                                      />
                                    )}
                                  </TableCell>
                                );
                              })}
                              <TableCell align="right">
                                <IconButton aria-label={t('delete')}
                                  color="error"
                                  disabled={isRowLocked}
                                  title={isRowLocked ? t('cannotDeleteAssignedUom', 'Cannot delete a UOM while it is selected as Base or Orderable') : undefined}
                                  onClick={() => handleRemoveUom(rowIndex)}
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box
                    sx={{
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Pagination
                      page={currentUomPage}
                      setPage={setUomPage}
                      maxPages={uomTotalPages}
                      breakpointForChangeDisplay={120000}
                      useNoDataMessage={false}
                    />
                  </Box>
                </Section>
              </Box>
            </Grid>

            {/* Supplier SKUs */}
            {tabValue === 6 && (
              <Grid item
                xs={12}>
                <Box sx={{ p: 4 }}>
                  {marketSuppliers?.length > 0 ? (
                    <Grid container
                      spacing={2}>
                      {marketSuppliers.map((s) => {
                        const existing = (formData.contentSuppliers || []).find(acc => acc.supplierId == s.supplierId);
                        return (
                          <Grid container
                            item
                            xs={12}
                            spacing={2}
                            alignItems="center"
                            key={s.supplierNumber}>
                            <Grid item
                              md={4}
                              xs={12}>
                              <Box fontWeight={500}>
                                {s.supplierName} - {s.supplierNumber}
                              </Box>
                            </Grid>
                            <Grid item
                              md={4}
                              xs={12}>
                              <TextField
                                label={t('itemNumber')}
                                value={existing?.itemNumber ?? ''}
                                onChange={e => {
                                  const value = e.target.value;
                                  setFormData(prev => {
                                    const current = prev.contentSuppliers || [];
                                    const i = current.findIndex(a => a.supplierId == s.supplierId);
                                    if (i >= 0) {
                                      const updated = [...current];
                                      updated[i] = { ...updated[i], itemNumber: value };
                                      return { ...prev, contentSuppliers: updated };
                                    }
                                    return { ...prev, contentSuppliers: [...current, { id: 0, contentId: 0, supplierId: s.supplierId, itemNumber: value, price: '' }] };
                                  });
                                }}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item
                              md={4}
                              xs={12}>
                              <TextField
                                label={t('price')}
                                type="number"
                                value={existing?.price ?? ''}
                                onChange={e => {
                                  const value = e.target.value;
                                  setFormData(prev => {
                                    const current = prev.contentSuppliers || [];
                                    const i = current.findIndex(a => a.supplierId == s.supplierId);
                                    if (i >= 0) {
                                      const updated = [...current];
                                      updated[i] = { ...updated[i], price: value };
                                      return { ...prev, contentSuppliers: updated };
                                    }
                                    return { ...prev, contentSuppliers: [...current, { id: 0, contentId: 0, supplierId: s.supplierId, itemNumber: '', price: value }] };
                                  });
                                }}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Typography variant="textSM"
                      color="text.secondary">
                      {t('noSuppliersAvailable', 'No suppliers available for the selected instance and market.')}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}

            {/* Store Quantity Limits */}
            {tabValue === 5 && isEditMode && formData.companyId > 0 && formData.customerTypeId && formData.customerTypeId > 0 && formData.itemNumber && (
              <Grid item
                xs={12}>
                <StoreLevelMOQGrid
                  companyId={formData.companyId}
                  customerTypeId={formData.customerTypeId}
                  itemNumber={formData.itemNumber}
                />
              </Grid>
            )}

            <Grid item
              xs={12}
              sx={{ display: tabValue === 7 ? 'block' : 'none' }}>
              <AllergensTab
                allergens={formData.allergens || []}
                onChange={(allergens) => setFormData(prev => ({ ...prev, allergens }))}
              />
            </Grid>

            {/* Ingredients Info: Demo Mode only - no backend persistence yet */}
            {isDemoMode && (
              <Grid item
                xs={12}
                sx={{ display: tabValue === 8 ? 'block' : 'none' }}>
                <IngredientsTab
                  ingredients={productIngredients}
                  onChange={setProductIngredients}
                />
              </Grid>
            )}

            <Grid item
              xs={12}
              sx={{ display: tabValue === (isDemoMode ? 9 : 8) ? 'block' : 'none' }}>
              <NutritionalDataTab
                nutrition={formData.nutrition || []}
                onChange={(nutrition) => setFormData(prev => ({ ...prev, nutrition }))}
              />
            </Grid>

            <Grid item
              xs={12}
              sx={{ display: tabValue === (isDemoMode ? 10 : 9) ? 'block' : 'none' }}>
              <DietaryAttributesTab
                dietaryAttributes={formData.dietaryAttributes || []}
                onChange={(dietaryAttributes) => setFormData(prev => ({ ...prev, dietaryAttributes }))}
              />
            </Grid>

          </Grid>
        </Box>
      </Section>

      {/* Success/Error Messages */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={5000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success"
          variant="filled">
          {t('successMessage')}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showErrorMessage}
        autoHideDuration={5000}
        onClose={() => setShowErrorMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error"
          variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>

      <ConfirmationDialog
        open={pendingBaseUomIndex !== null}
        icon={<WarningAmberIcon />}
        variant="bad"
        title={t('changeBaseUnitTitle', 'Change Base Unit of Measure?')}
        message={t(
          'changeBaseUnitWarning',
          'Changing the base unit of measure has significant repercussions and will negatively impact historic reporting, stock on hand, and pricing calculations.',
        )}
        confirmButtonText={t('changeBaseUnitConfirm', 'Change Base Unit')}
        cancelButtonText={t('cancel', 'Cancel')}
        onConfirm={confirmBaseUomChange}
        onCancel={cancelBaseUomChange}
      />

      <ConfirmationDialog
        open={pendingOrderableUomIndex !== null}
        icon={<WarningAmberIcon />}
        variant="bad"
        title={t('changeOrderableUnitTitle', 'Change Orderable Unit of Measure?')}
        message={t(
          'changeOrderableUnitWarning',
          'Changing the orderable unit of measure will affect ordering, receiving, and pricing for this item going forward.',
        )}
        confirmButtonText={t('changeOrderableUnitConfirm', 'Change Orderable Unit')}
        cancelButtonText={t('cancel', 'Cancel')}
        onConfirm={confirmOrderableUomChange}
        onCancel={cancelOrderableUomChange}
      />

      <ConfirmationDialog
        open={pendingRemoveUomIndex !== null}
        icon={<WarningAmberIcon />}
        variant="bad"
        title={t('removeUomTitle', 'Remove Unit of Measure?')}
        message={t(
          'removeUomWarning',
          'Completely removing a historically ordered unit of measure may impact stores\u2019 ability to receive outstanding deliveries.',
        )}
        confirmButtonText={t('removeUomConfirm', 'Remove')}
        cancelButtonText={t('cancel', 'Cancel')}
        onConfirm={confirmRemoveUom}
        onCancel={cancelRemoveUom}
      />
    </DetailPageTemplate>
  );
};

export default ProductForm;
